// app/frontend/components/EventForm/sections/EventDetailsSection.tsx
import { Accordion, Group, Text, MultiSelect, Select } from '@mantine/core';
import { IconList } from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { EventDetailsSectionProps } from '../types';
import ImageUploadModal from '../../ImageUploadModal';
import { sortByName, sortEventFrequencies } from '../utils/formHelpers';

const EventDetailsSection: React.FC<EventDetailsSectionProps> = ({
  form,
  isDraft,
  editor,
  eventTypes,
  danceStyles,
  eventFrequencies,
  seoImagePreview,
  onImageChange,
}) => {
  // Sort arrays alphabetically
  const sortedEventTypes = sortByName(eventTypes);
  const sortedDanceStyles = sortByName(danceStyles);
  const sortedEventFrequencies = sortEventFrequencies(eventFrequencies);

  return (
    <Accordion multiple className="form-accordion">
      <Accordion.Item value="event-details">
        <Accordion.Control>
          <Group gap="xs">
            <IconList size={28} />
            <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Details</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <ImageUploadModal previewUrl={seoImagePreview} onImageChange={onImageChange} />

          <MultiSelect
            label="What type of event is this?"
            comboboxProps={{ shadow: 'md' }}
            name="event_type_ids"
            hidePickedOptions
            styles={{ label: { color: '#FFF7ED' } }}
            placeholder={
              form.values.event_type_ids && form.values.event_type_ids.length > 0 ? '' : 'Choose your event types'
            }
            data={sortedEventTypes.map((type) => ({
              value: type.id.toString(),
              label: type.name,
            }))}
            value={
              // Ensure we always have an array and convert numbers to strings
              (Array.isArray(form.values.event_type_ids) ? form.values.event_type_ids : []).map((id: number | string) =>
                id.toString(),
              )
            }
            onChange={(values) => {
              // Convert the selected string IDs back to numbers
              const numericValues = values.map(Number);
              form.setFieldValue('event_type_ids', numericValues);
            }}
            searchable
            clearable
            required={!isDraft}
          />

          <MultiSelect
            label="Dance Styles"
            name="dance_style_ids"
            comboboxProps={{ shadow: 'md' }}
            hidePickedOptions
            className="select-dropdown"
            styles={{
              label: { color: '#FFF7ED' },
              dropdown: { zIndex: 1000 },
            }}
            placeholder={
              form.values.dance_style_ids && form.values.dance_style_ids.length > 0 ? '' : 'Choose dance styles'
            }
            data={sortedDanceStyles.map((style) => ({
              value: style.id.toString(),
              label: style.name,
            }))}
            value={(Array.isArray(form.values.dance_style_ids) ? form.values.dance_style_ids : []).map(
              (id: number | string) => id.toString(),
            )}
            onChange={(values) => {
              // Convert the selected string IDs back to numbers
              const numericValues = values.map(Number);
              form.setFieldValue('dance_style_ids', numericValues);
            }}
            searchable
            clearable
            mt="lg"
            required={!isDraft}
          />

          <Select
            label="Event Frequency"
            name="event_frequency_id"
            styles={{ label: { color: '#FFF7ED' } }}
            placeholder="Select event frequency"
            data={sortedEventFrequencies.map((freq) => ({
              value: freq.id.toString(),
              label: freq.name,
            }))}
            value={form.values.event_frequency_id ? form.values.event_frequency_id.toString() : null}
            onChange={(value) => {
              form.setFieldValue('event_frequency_id', value ? Number(value) : null);
              // Validate this field immediately after change
              form.validateField('event_frequency_id');
            }}
            onBlur={() => form.validateField('event_frequency_id')}
            error={form.errors.event_frequency_id}
            searchable
            clearable
            mt="lg"
            mb="xl"
            required={!isDraft}
          />

          <Text size="sm" fw={500} style={{ color: '#FFF7ED' }}>
            Event Description
          </Text>
          {editor && (
            <RichTextEditor editor={editor} mt="" className="rich-text-editor">
              <RichTextEditor.Toolbar>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Code />
                  <RichTextEditor.Highlight />
                  <RichTextEditor.ClearFormatting />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.H1 />
                  <RichTextEditor.H2 />
                  <RichTextEditor.H3 />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Blockquote />
                  <RichTextEditor.Hr />
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Blockquote />
                  <RichTextEditor.OrderedList />
                  <RichTextEditor.BulletList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Undo />
                  <RichTextEditor.Redo />
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content />
            </RichTextEditor>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default EventDetailsSection;
